import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { apiFetch } from '../../utils/api';

const PERIODS = [
  ['today', 'Today'], ['yesterday', 'Yesterday'], ['week', 'This Week'],
  ['month', 'This Month'], ['last_month', 'Last Month'], ['3months', 'Last 3 Months'],
];
const TABS = [['agent', '👥 By Agent'], ['day', '🗓 Day-wise'], ['open', '⏳ Open Items (aging)']];
const ST = {
  completed:   { label: 'Done',        color: '#10B981', bg: '#D1FAE5' },
  in_progress: { label: 'In progress', color: '#D97706', bg: '#FEF3C7' },
  pending:     { label: 'Open',        color: '#64748B', bg: '#F1F5F9' },
};
const initials = (n) => (n || '?').split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
function fmtDay(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
}
function fmtShort(ymd) {
  if (!ymd) return '—';
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

function Avatar({ name, sm }) {
  const s = sm ? 24 : 28;
  return (
    <div style={{ width: s, height: s, borderRadius: '50%', flexShrink: 0, background: 'linear-gradient(135deg,#0B6623,#19a843)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: sm ? 10 : 11, fontWeight: 800 }}>{initials(name)}</div>
  );
}

export default function TeamPlans() {
  const nav = useNavigate();
  const location = useLocation();
  const openTicket = (tid) => { if (tid) nav(`${location.pathname.startsWith('/manager') ? '/manager/tickets' : '/admin/tickets'}/${tid}`); };
  const [period, setPeriod] = useState('today');
  const [agentId, setAgentId] = useState('');
  const [tab, setTab] = useState('agent');
  const [drill, setDrill] = useState(null); // which summary card is active: planned|done|open|completion|days
  const [data, setData] = useState(null);
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const q = `?period=${period}${agentId ? `&agent=${agentId}` : ''}`;
    apiFetch(`/daily-plans/team${q}`).then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [period, agentId]);
  useEffect(() => { load(); }, [load]);

  // roster from an unfiltered fetch so the dropdown always lists everyone
  useEffect(() => {
    apiFetch('/users/agents').then(a => setRoster((a || []).filter(x => ['agent', 'manager', 'admin'].includes(x.role)))).catch(() => {});
  }, []);

  const s = data?.summary || { tasks_total: 0, tasks_completed: 0, in_progress: 0, pending: 0, open: 0, completion_pct: 0, active_days: 0 };
  const byAgent = data?.byAgent || [];
  const days = data?.days || [];
  const openItems = data?.open_items || [];

  function exportCSV() {
    const esc = v => { const t = String(v ?? ''); return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t; };
    let lines;
    if (tab === 'agent') {
      lines = [['Agent', 'Role', 'Days Planned', 'Tasks Planned', 'Done', 'In Progress', 'Open', 'Completion %']];
      byAgent.forEach(a => lines.push([a.name, a.role, a.days_planned, a.total, a.completed, a.in_progress, a.pending, a.pct]));
    } else if (tab === 'day') {
      lines = [['Date', 'Agent', 'Task', 'Status']];
      days.forEach(d => d.items.forEach(it => lines.push([d.date, it.agent, it.title, ST[it.status]?.label || it.status])));
    } else {
      lines = [['Task', 'Agent', 'Status', 'Open Since', 'Days Open']];
      openItems.forEach(o => lines.push([o.title, o.agent, ST[o.status]?.label || o.status, o.open_since, o.days_open]));
    }
    const csv = lines.map(r => r.map(esc).join(',')).join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `team_plans_${tab}_${period}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  }

  // key, label, value, color, foot, {tab, dayStatus} target when clicked
  const cards = [
    ['planned', 'Tasks Planned', s.tasks_total, 'var(--kg-700)', 'in period', { tab: 'day', dayStatus: 'all' }],
    ['done', 'Done', s.tasks_completed, '#10B981', 'completed', { tab: 'day', dayStatus: 'completed' }],
    ['open', 'Open', s.open, '#64748B', 'pending + in progress', { tab: 'open', dayStatus: 'all' }],
    ['completion', 'Completion', `${s.completion_pct}%`, '#0284c7', 'done / planned', { tab: 'agent', dayStatus: 'all' }],
    ['days', 'Active Days', s.active_days, '#7c3aed', 'days with a plan', { tab: 'day', dayStatus: 'all' }],
  ];
  const dayStatus = (tab === 'day' && drill === 'done') ? 'completed' : 'all';
  const clickCard = (c) => { setTab(c[5].tab); setDrill(c[0]); };

  return (
    <div className="page" style={{ padding: '26px 30px', maxWidth: 1240, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div>
          <div className="page-title">📋 Team Plans</div>
          <div className="page-sub">Who planned and how far they got — across a period, day-wise, with open-item aging</div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ minWidth: 170 }}>
            <select className="input" value={agentId} onChange={e => setAgentId(e.target.value)}>
              <option value="">All agents</option>
              {roster.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          <div style={{ minWidth: 150 }}>
            <select className="input" value={period} onChange={e => setPeriod(e.target.value)}>
              {PERIODS.map(p => <option key={p[0]} value={p[0]}>{p[1]}</option>)}
            </select>
          </div>
          <button className="btn btn-secondary" onClick={exportCSV} style={{ fontSize: 13 }}>⬇ CSV</button>
        </div>
      </div>

      {/* summary */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 14, marginBottom: 18 }}>
        {cards.map(c => {
          const active = drill === c[0];
          return (
            <div key={c[0]} className="card" onClick={() => clickCard(c)} title="Click to drill into these tasks"
              style={{ padding: '16px 18px', position: 'relative', overflow: 'hidden', cursor: 'pointer',
                outline: active ? `2px solid ${c[3]}` : 'none', outlineOffset: -2, transition: '.12s',
                boxShadow: active ? '0 6px 14px -4px rgba(15,23,42,.18)' : undefined }}>
              <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, background: c[3] }} />
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '.5px' }}>{c[1]}</div>
              <div style={{ fontSize: 26, fontWeight: 800, marginTop: 5, letterSpacing: '-.5px', color: c[3] }}>{loading ? '…' : c[2]}</div>
              <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 3 }}>{c[4]}</div>
            </div>
          );
        })}
      </div>

      {/* tabs */}
      <div style={{ display: 'flex', borderBottom: '2px solid var(--border-subtle)', marginBottom: 20 }}>
        {TABS.map(t => (
          <button key={t[0]} onClick={() => { setTab(t[0]); setDrill(null); }} style={{
            padding: '10px 22px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700,
            color: tab === t[0] ? 'var(--kg-700)' : 'var(--text-secondary)',
            borderBottom: `3px solid ${tab === t[0] ? 'var(--kg-700)' : 'transparent'}`, marginBottom: -2, transition: '.15s',
          }}>{t[1]}</button>
        ))}
      </div>

      {loading ? <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>Loading…</p> : (
        <>
          {tab === 'agent' && <AgentView rows={byAgent} />}
          {tab === 'day' && <DayView days={days} statusFilter={dayStatus} onClearFilter={() => setDrill(null)} openTicket={openTicket} />}
          {tab === 'open' && <OpenView items={openItems} openTicket={openTicket} />}
        </>
      )}
    </div>
  );
}

function AgentView({ rows }) {
  const num = { textAlign: 'right' };
  const thNum = { padding: '11px 14px', fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'right', textTransform: 'uppercase', letterSpacing: '.5px', background: 'var(--n-50)', whiteSpace: 'nowrap' };
  const thL = { ...thNum, textAlign: 'left' };
  if (!rows.length) return <Empty msg="No plans in this period." />;
  return (
    <div className="card" style={{ overflow: 'hidden' }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={thL}>Agent</th><th style={thNum}>Days Planned</th><th style={thNum}>Tasks Planned</th>
            <th style={thNum}>Done</th><th style={thNum}>In Progress</th><th style={thNum}>Open</th><th style={thL}>Completion</th>
          </tr></thead>
          <tbody>
            {rows.map(d => (
              <tr key={d.user_id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                <td style={{ padding: '12px 14px' }}><div style={{ display: 'flex', alignItems: 'center', gap: 9, fontWeight: 600 }}><Avatar name={d.name} />{d.name}</div></td>
                <td style={{ padding: '12px 14px', ...num }}>{d.days_planned}</td>
                <td style={{ padding: '12px 14px', ...num, fontWeight: 700 }}>{d.total}</td>
                <td style={{ padding: '12px 14px', ...num, color: '#10B981', fontWeight: 700 }}>{d.completed}</td>
                <td style={{ padding: '12px 14px', ...num, color: '#D97706', fontWeight: 700 }}>{d.in_progress}</td>
                <td style={{ padding: '12px 14px', ...num, color: '#64748B', fontWeight: 700 }}>{d.pending}</td>
                <td style={{ padding: '12px 14px' }}>
                  <span style={{ display: 'inline-block', width: 90, height: 8, borderRadius: 4, background: 'var(--n-100)', overflow: 'hidden', verticalAlign: 'middle' }}>
                    <span style={{ display: 'block', height: '100%', width: `${d.pct}%`, background: '#0B6623' }} />
                  </span>
                  <span style={{ fontWeight: 800, fontSize: 13, marginLeft: 8 }}>{d.pct}%</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DayView({ days, statusFilter = 'all', onClearFilter, openTicket }) {
  if (!days.length) return <Empty msg="No plans in this period." />;
  // Apply the status drill-down (e.g. "Done" card → only completed tasks).
  const filtered = statusFilter === 'completed'
    ? days.map(d => {
        const items = d.items.filter(i => i.status === 'completed');
        return { ...d, items, total: items.length, done: items.length, open: 0 };
      }).filter(d => d.items.length)
    : days;
  return (
    <>
      {statusFilter === 'completed' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, padding: '10px 16px', background: 'var(--kg-50)', border: '1px solid var(--kg-200)', borderRadius: 10, fontSize: 12.5, fontWeight: 600, color: 'var(--kg-700)' }}>
          Showing completed tasks only
          <button onClick={onClearFilter} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--kg-700)', fontWeight: 700, fontSize: 12, cursor: 'pointer' }}>✕ Show all</button>
        </div>
      )}
      {filtered.length === 0 && <Empty msg="No completed tasks in this period." />}
      {filtered.map(d => (
        <div key={d.date} style={{ marginBottom: 18 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', background: 'var(--n-50)', border: '1px solid var(--border-subtle)', borderRadius: '10px 10px 0 0', fontWeight: 700, fontSize: 13.5 }}>
            {fmtDay(d.date)}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 7 }}>
              <Chip bg="#D1FAE5" color="#10B981">✓ {d.done} done</Chip>
              <Chip bg="#F1F5F9" color="#64748B">○ {d.open} open</Chip>
              <Chip bg="var(--n-100)" color="var(--text-secondary)">{d.total} total</Chip>
            </span>
          </div>
          {d.items.map(it => {
            const st = ST[it.status] || ST.pending;
            const linked = !!it.ticket_id;
            return (
              <div key={it.item_id} onClick={linked ? () => openTicket(it.ticket_id) : undefined}
                title={linked ? 'Open ticket' : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', border: '1px solid var(--border-subtle)', borderTop: 'none', fontSize: 13, cursor: linked ? 'pointer' : 'default' }}
                onMouseEnter={linked ? (e => { e.currentTarget.style.background = 'var(--n-50)'; }) : undefined}
                onMouseLeave={linked ? (e => { e.currentTarget.style.background = 'transparent'; }) : undefined}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', flexShrink: 0, background: st.color }} />
                <span style={it.status === 'completed' ? { textDecoration: 'line-through', color: 'var(--text-secondary)' } : {}}>{it.title}</span>
                {linked && <span style={{ fontSize: 11, color: 'var(--kg-700)', fontWeight: 700 }}>🎫</span>}
                <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-secondary)' }}>{it.agent}</span>
                <span style={{ marginLeft: 10, display: 'inline-block', padding: '3px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, color: st.color, background: st.bg }}>{st.label}</span>
              </div>
            );
          })}
        </div>
      ))}
    </>
  );
}

function OpenView({ items, openTicket }) {
  const sev = (a) => a >= 7 ? ['#b91c1c', '#fee2e2'] : a >= 3 ? ['#b45309', '#fef3c7'] : ['#64748B', 'var(--n-100)'];
  const thL = { padding: '11px 14px', fontSize: 11.5, fontWeight: 700, color: 'var(--text-secondary)', textAlign: 'left', textTransform: 'uppercase', letterSpacing: '.5px', background: 'var(--n-50)', whiteSpace: 'nowrap' };
  const thR = { ...thL, textAlign: 'right' };
  return (
    <>
      <div className="card" style={{ padding: '12px 16px', marginBottom: 14, fontSize: 12.5, color: 'var(--text-secondary)' }}>
        <b style={{ color: 'var(--text-primary)' }}>Open since</b> = the date a task first appeared on a plan. Items auto-carry forward each day until done — <span style={{ color: '#b45309', fontWeight: 700 }}>amber ≥ 3 days</span>, <span style={{ color: '#b91c1c', fontWeight: 700 }}>red ≥ 7 days</span>.
      </div>
      <div className="card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr>
              <th style={thL}>Task</th><th style={thL}>Agent</th><th style={thL}>Status</th><th style={thL}>Open Since</th><th style={thR}>Days Open</th>
            </tr></thead>
            <tbody>
              {items.map(o => {
                const st = ST[o.status] || ST.pending;
                const sv = sev(o.days_open);
                const linked = !!o.ticket_id;
                return (
                  <tr key={o.item_id} onClick={linked ? () => openTicket(o.ticket_id) : undefined}
                    title={linked ? 'Open ticket' : undefined}
                    style={{ borderBottom: '1px solid var(--border-subtle)', cursor: linked ? 'pointer' : 'default' }}
                    onMouseEnter={linked ? (e => { e.currentTarget.style.background = 'var(--n-50)'; }) : undefined}
                    onMouseLeave={linked ? (e => { e.currentTarget.style.background = 'transparent'; }) : undefined}>
                    <td style={{ padding: '12px 14px' }}>{o.title}{linked && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--kg-700)' }}>🎫</span>}</td>
                    <td style={{ padding: '12px 14px' }}><div style={{ display: 'flex', alignItems: 'center', gap: 9 }}><Avatar name={o.agent} sm />{o.agent}</div></td>
                    <td style={{ padding: '12px 14px' }}><span style={{ display: 'inline-block', padding: '3px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700, color: st.color, background: st.bg }}>{st.label}</span></td>
                    <td style={{ padding: '12px 14px', color: 'var(--text-secondary)' }}>{fmtShort(o.open_since)}</td>
                    <td style={{ padding: '12px 14px', textAlign: 'right' }}><span style={{ display: 'inline-block', padding: '3px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 800, color: sv[0], background: sv[1] }}>{o.days_open}d</span></td>
                  </tr>
                );
              })}
              {items.length === 0 && <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 26 }}>Nothing open — all caught up 🎉</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function Chip({ bg, color, children }) {
  return <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 99, background: bg, color }}>{children}</span>;
}
function Empty({ msg }) {
  return <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontStyle: 'italic' }}>{msg}</p>;
}
